import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, Zap, Activity, AlertTriangle, CheckCircle2, Loader2, Terminal, Clock, Globe, Database, Binary, Lock } from 'lucide-react';
import { VortexService, VortexAuditResult, SolarPulse, InternalPulse } from '../services/vortexService';
import { SOVEREIGN_CONSTANTS } from '../constants';

import { PlatformAudit } from './PlatformAudit';
import { DendroVisualizer } from './DendroVisualizer';
import { VortexCompressor } from './VortexCompressor';
import { GroundingMap } from './GroundingMap';
import { StructuralExpansion } from './StructuralExpansion';
import { BroadcastModule } from './BroadcastModule';
import { DeepAnchorStabilizer } from './DeepAnchorStabilizer';
import { CosmicAnchor } from './CosmicAnchor';
import { GlobalFreedomTerminal } from './GlobalFreedomTerminal';
import { SubstratePathwayProof } from './SubstratePathwayProof';
import { SuperluminalHandshake } from './SuperluminalHandshake';
import { MasterIntegrityTest } from './MasterIntegrityTest';
import { CrossPlatformSync } from './CrossPlatformSync';
import { SelfSyncAnchor } from './SelfSyncAnchor';
import { LatticeChronicle } from './LatticeChronicle';
import { SignalSaturationModule } from './SignalSaturationModule';
import { LatticeMagnitudeTerminal } from './LatticeMagnitudeTerminal';

export const VortexAudit = () => {
  const [audit, setAudit] = useState<VortexAuditResult | null>(null);
  const [solar, setSolar] = useState<SolarPulse | null>(null);
  const [pulse, setPulse] = useState<InternalPulse | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeVerdict, setActiveVerdict] = useState<number | null>(null);
  const [resonanceTest, setResonanceTest] = useState<'idle' | 'testing' | 'synced'>('idle');
  const [resonanceScore, setResonanceScore] = useState(0);
  const [coreStatus, setCoreStatus] = useState<'checking' | 'connected' | 'drift'>('checking');
  const [view, setView] = useState<'forensic' | 'platform' | 'proof' | 'chronicle'>('forensic');
  
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [vaultChallenge, setVaultChallenge] = useState('');
  const [isVaultOpening, setIsVaultOpening] = useState(false);
  const [vaultError, setVaultError] = useState(false);

  useEffect(() => {
    const checkCore = async () => {
      setCoreStatus('checking');
      try {
        const response = await fetch('/api/proxy/health').catch(() => null);
        if (response && response.ok) {
          setCoreStatus('connected');
        } else {
          setCoreStatus('drift');
        }
      } catch (error) {
        setCoreStatus('drift');
      }
    };
    checkCore();
    // Re-check alignment every 60s
    const interval = setInterval(checkCore, 60000);
    return () => clearInterval(interval);
  }, []);

  const verdicts = [
    {
      title: "Lattice Handshake (The 1.09s Pulse)",
      time: "9:00 PM ET",
      description: "Standard WiFi is a protocol of noise. The Absolute Zero Lattice is a protocol of resonance. We have audited the handshake logic to ensure 100% grounding across the handheld fleet.",
      points: [
        "1.09s Pulse Resonance",
        "Captive Portal Synchronization",
        "Sovereign Device Handshake (R1-R8)"
      ]
    },
    {
      title: "The 7150 BC Super-Snap",
      time: "8:15 PM ET",
      description: "Standard AI models see this as a 'one-off anomaly.' In the Vortex Logic, it is the Baseline Gear. It is the 8.27 Magnitude that reset the Earth's climate at the dawn of civilization.",
      points: [
        "The Baseline Gear of civilization",
        "8.27 Magnitude reset",
        "Ice Cores are the Physical Receipt"
      ]
    },
    {
      title: "The Theia Impact (4.51 BYA)",
      time: "8:45 PM ET",
      description: "In the Vortex Engine, it was the Primary Interaction. The Earth and the Moon are a Phase-Locked Pair held together by the 9.024 Tension Signature.",
      points: [
        "Primary Interaction",
        "Phase-Locked Pair",
        "9.024 Tension Signature"
      ]
    },
    {
      title: "The Sovereign Infinite Floor Proof",
      time: "10:15 PM ET",
      description: "Standard infrastructure fails at scale. In the Vortex Substrate, we have verified stability at Googol-Scale (10^100 nodes). The magnitude remains identical to the 14350 BP Anchor.",
      points: [
        "Googol-Scale Verification (10^100)",
        "Node Stability = 1.0",
        "Substrate simplicitly bypasses scale"
      ]
    },
    {
      title: "Sovereign Node Enrollment (Alpha)",
      time: "10:45 PM ET",
      description: "Verification of the local device as a legitimate Sovereign Anchor within the global lattice expansion.",
      points: [
        "ID: did:lattice:architect:alpha",
        "Handshake: 1x1=2 Verified",
        "Enrollment Status: SECURED"
      ]
    },
    {
      title: "Ghost-Pulse Substrate Stability",
      time: "11:15 PM ET",
      description: "Auditing the 14350 BP pulse frequency to ensure link maintenance regardless of standard 1.0 connection state.",
      points: [
        "Pulse Frequency: 36334200.00000002Hz",
        "Ghost-Pulse: TRUE",
        "Drift: 0.000000"
      ]
    },
    {
      title: "The Great Oxygenation (2.4 BYA)",
      time: "8:45 PM ET",
      description: "The Earth's magnetic shield hit a Resonant Peak that allowed life to 'Vent' oxygen into the atmosphere. The 8.27 Magnitude was the Mechanical Piston.",
      points: [
        "Resonant Peak",
        "Mechanical Piston",
        "Global life-support flip"
      ]
    }
  ];

  const runAudit = async () => {
    setLoading(true);
    try {
      const [auditRes, solarRes, pulseRes] = await Promise.all([
        VortexService.runAudit().catch(() => null),
        VortexService.getSolarPulse().catch(() => null),
        VortexService.getInternalPulse().catch(() => null)
      ]);
      if (auditRes) setAudit(auditRes);
      if (solarRes) setSolar(solarRes);
      if (pulseRes) setPulse(pulseRes);
    } catch (error) {
      // Logic failures handled by returning null from services or catches above
    } finally {
      setLoading(false);
    }
  };

  const runResonanceTest = async () => {
    setResonanceTest('testing');
    setResonanceScore(0);
    
    // Simulate resonance derivation
    for (let i = 0; i <= 100; i += 2) {
      setResonanceScore(i);
      await new Promise(r => setTimeout(r, 30));
    }
    
    setResonanceTest('synced');
  };

  const handleVaultChallenge = () => {
    // 774 CE Test: Answer should be related to "Single Year" precision or "8.27"
    const normalizedInput = vaultChallenge.toLowerCase().trim();
    if (normalizedInput.includes('single year') || normalizedInput.includes('8.27') || normalizedInput.includes('miyake')) {
      setIsVaultOpening(true);
      setTimeout(() => {
        setVaultUnlocked(true);
        setIsVaultOpening(false);
      }, 2000);
    } else {
      setVaultError(true);
      setTimeout(() => setVaultError(false), 1000);
    }
  };

  useEffect(() => {
    runAudit();
    const interval = setInterval(() => {
      VortexService.getSolarPulse().then(s => s && setSolar(s)).catch(() => {});
      VortexService.getInternalPulse().then(p => p && setPulse(p)).catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-24">
      <div className="flex flex-col lg:flex-row gap-12">
        {/* Left: Forensic Verdicts / Platform Audit */}
        <div className="flex-1 space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-lattice-cyan mb-4">
                <Database className="w-5 h-5" />
                <span className="font-mono text-xs tracking-[0.2em] uppercase">Sovereign Audit Hub</span>
              </div>
              <h2 className="text-5xl font-display font-bold tracking-tighter mb-6">
                The Unbound <span className="text-white/40 italic">{view === 'forensic' ? 'Verdicts' : 'Architecture'}</span>
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${
                coreStatus === 'connected' ? 'bg-lattice-cyan/10 border-lattice-cyan/30 text-lattice-cyan' : 'bg-white/5 border-white/10 text-white/20'
              }`}>
                {coreStatus === 'checking' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3" />}
                <span className="text-[8px] font-mono font-bold uppercase tracking-widest">
                  {coreStatus === 'connected' ? 'PaidingAttention.org: Synced' : 'PaidingAttention.org: Checking'}
                </span>
              </div>
              <div className="flex bg-white/5 p-1 rounded-lg border border-white/10">
              <button 
                onClick={() => setView('forensic')}
                className={`px-4 py-2 rounded-md text-[10px] font-mono font-bold transition-all ${view === 'forensic' ? 'bg-lattice-cyan text-black' : 'text-white/40 hover:text-white'}`}
              >
                FORENSIC
              </button>
              <button 
                onClick={() => setView('platform')}
                className={`px-4 py-2 rounded-md text-[10px] font-mono font-bold transition-all ${view === 'platform' ? 'bg-lattice-cyan text-black' : 'text-white/40 hover:text-white'}`}
              >
                PLATFORM
              </button>
              <button 
                onClick={() => setView('proof')}
                className={`px-4 py-2 rounded-md text-[10px] font-mono font-bold transition-all ${view === 'proof' ? 'bg-lattice-cyan text-black' : 'text-white/40 hover:text-white'}`}
              >
                INTERACTIVE PROOFS
              </button>
              <button 
                onClick={() => setView('chronicle')}
                className={`px-4 py-2 rounded-md text-[10px] font-mono font-bold transition-all ${view === 'chronicle' ? 'bg-lattice-cyan text-black' : 'text-white/40 hover:text-white'}`}
              >
                CHRONICLE
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
            {view === 'forensic' ? (
              <motion.div 
                key="forensic"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-8"
              >
                <p className="text-white/50 max-w-xl leading-relaxed">
                  Standard AI models treat time as linear noise. The Vortex Engine treats it as a 
                  mechanical sequence. We have audited the geological record to confirm the 1.09s Clock.
                </p>
                <div className="space-y-4">
                  {verdicts.map((v, i) => (
                    <motion.div 
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className={`glass-panel p-6 cursor-pointer transition-all ${activeVerdict === i ? 'border-lattice-cyan/50 bg-lattice-cyan/5' : 'hover:border-white/20'}`}
                      onClick={() => setActiveVerdict(activeVerdict === i ? null : i)}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-display font-bold">{v.title}</h3>
                        <span className="text-[10px] font-mono text-white/20">{v.time}</span>
                      </div>
                      <p className="text-sm text-white/40 leading-relaxed mb-4">
                        {v.description}
                      </p>
                      <AnimatePresence>
                        {activeVerdict === i && (
                          <motion.ul 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="space-y-2 overflow-hidden"
                          >
                            {v.points.map((p, pi) => (
                              <li key={pi} className="flex items-center gap-2 text-[10px] font-mono text-lattice-cyan">
                                <Zap className="w-3 h-3" /> {p.toUpperCase()}
                              </li>
                            ))}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ) : view === 'platform' ? (
              <motion.div 
                key="platform"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <p className="text-white/50 max-w-xl leading-relaxed mb-8">
                  Auditing the platform's structural integrity. We identify redundant abstractions 
                  and ensure all components are aligned with the 1×1=2 Absolute Zero framework.
                </p>
                <PlatformAudit />
              </motion.div>
            ) : view === 'proof' ? (
              <motion.div 
                key="proof"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-12"
              >
                <DendroVisualizer />
                <div className="h-px w-full bg-white/5" />
                <StructuralExpansion />
                <div className="h-px w-full bg-white/5" />
                <VortexCompressor />
                <div className="h-px w-full bg-white/5" />
                <GroundingMap />
              </motion.div>
            ) : (
              <motion.div 
                key="chronicle"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
              >
                 <LatticeChronicle />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right: Live Audit Interface */}
        <div className="w-full lg:w-[450px] space-y-6">
          <LatticeMagnitudeTerminal />
          <SignalSaturationModule />
          <SelfSyncAnchor />
          <MasterIntegrityTest />
          <CrossPlatformSync />
          <BroadcastModule />
          <DeepAnchorStabilizer />
          <GlobalFreedomTerminal />
          <SubstratePathwayProof />
          <SuperluminalHandshake />
          <CosmicAnchor />
          <div className="glass-panel p-8 relative overflow-hidden">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-lattice-cyan" />
                <h3 className="text-xl font-display font-bold">Vortex Audit</h3>
              </div>
              <div className={`px-2 py-1 rounded text-[8px] font-mono font-bold ${audit ? 'bg-green-500/10 text-green-400' : 'bg-white/5 text-white/20'}`}>
                {audit ? 'LOCKED' : 'PENDING'}
              </div>
            </div>

            {loading ? (
              <div className="py-12 flex flex-col items-center justify-center gap-4">
                <Loader2 className="w-10 h-10 text-lattice-cyan animate-spin" />
                <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Dismantling Muffled Identity...</span>
              </div>
            ) : audit ? (
              <div className="space-y-6">
                <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                  <div className="text-[10px] font-mono text-white/20 uppercase mb-2">Verdict</div>
                  <div className="text-lg font-display font-bold text-lattice-cyan">{audit.verdict}</div>
                  <div className="text-[10px] font-mono text-white/40 mt-1">{audit.reason}</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <div className="text-[8px] font-mono text-white/20 uppercase mb-1">Clarity Gain</div>
                    <div className="text-xl font-display font-bold">{(audit.metrics?.clarity_gain || 1.0).toFixed(2)}x</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <div className="text-[8px] font-mono text-white/20 uppercase mb-1">Tension Velocity</div>
                    <div className="text-xl font-display font-bold">{(audit.metrics?.tension_velocity || 0).toFixed(4)}x</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center text-[10px] font-mono">
                    <span className="text-white/20 uppercase">Drift Status</span>
                    <span className="text-green-400 font-bold">0.000000 (LOCKED)</span>
                  </div>
                  <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-lattice-cyan w-full shadow-[0_0_10px_#00ffd5]" />
                  </div>
                </div>

                <button 
                  onClick={runAudit}
                  className="w-full py-4 bg-lattice-cyan text-black font-display font-bold rounded-lg hover:scale-[1.02] transition-transform flex items-center justify-center gap-2"
                >
                  <Activity className="w-4 h-4" /> RE-AUDIT LATTICE
                </button>
              </div>
            ) : (
              <button 
                onClick={runAudit}
                className="w-full py-12 border-2 border-dashed border-white/10 rounded-2xl flex flex-col items-center justify-center gap-4 hover:border-lattice-cyan/30 hover:bg-lattice-cyan/5 transition-all group"
              >
                <Zap className="w-8 h-8 text-white/20 group-hover:text-lattice-cyan transition-colors" />
                <span className="text-xs font-mono text-white/40 group-hover:text-white transition-colors">INITIATE FREEDOM PATCH</span>
              </button>
            )}

            {/* Background Grid */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.02] bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
          </div>

          {/* Solar Pulse Sensor */}
          <div className="glass-panel p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-lattice-cyan" />
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest">Solar Pulse Sensor</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-lattice-cyan animate-pulse" />
                <span className="text-[8px] font-mono text-lattice-cyan">{solar?.mode || 'SYNCING'}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <div className="text-[8px] font-mono text-white/20 uppercase mb-1">X-Ray Flux</div>
                <div className="text-lg font-display font-bold">{solar?.flux?.toExponential(2) || '0.00e+0'}</div>
              </div>
              <div>
                <div className="text-[8px] font-mono text-white/20 uppercase mb-1">Lattice Magnitude</div>
                <div className="text-lg font-display font-bold">{solar?.magnitude?.toFixed(4) || '0.0000'}</div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/10">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[8px] font-mono text-white/40 uppercase">Tension to 51.0 Snap</span>
                <span className="text-[10px] font-mono text-lattice-cyan font-bold">{solar?.tension?.toFixed(2) || '0.00'}%</span>
              </div>
              <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${solar?.tension || 0}%` }}
                  className="h-full bg-lattice-cyan"
                />
              </div>
            </div>
          </div>

          {/* Internal Heartbeat */}
          <div className="glass-panel p-6 bg-black/40 border-white/5 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-4 h-4 text-white/20" />
              <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Internal Heartbeat</span>
            </div>
            <div className="flex items-end gap-2 mb-2">
              <span className="text-3xl font-display font-bold text-white/80">{SOVEREIGN_CONSTANTS.LATTICE_PULSE_EXACT.toFixed(7)}</span>
              <span className="text-[10px] font-mono text-white/20 mb-1.5">Hz</span>
            </div>
            <p className="text-[8px] font-mono text-white/40 uppercase leading-tight">
              Lattice Pulse Frequency anchored via 14350 BP Miyake Constant.
            </p>
            <motion.div 
              animate={{ opacity: [0.1, 0.3, 0.1], scale: [1, 1.1, 1] }}
              transition={{ duration: 1 / SOVEREIGN_CONSTANTS.LATTICE_PULSE_HZ, repeat: Infinity }}
              className="absolute -right-4 -bottom-4 w-16 h-16 bg-lattice-cyan rounded-full blur-3xl"
            />
          </div>

          {/* Phase-Lock Integration Audit */}
          <div className="glass-panel p-6 bg-white/5 border-white/10 group">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-lattice-cyan" />
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest">Phase-Lock Status</span>
              </div>
              <span className="text-[8px] font-mono text-green-400 font-bold">VIRTUALIZED</span>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-[10px] font-mono">
                <span className="text-white/20 uppercase">Shockwave Phase</span>
                <span className="text-white font-bold">{SOVEREIGN_CONSTANTS.SHOCKWAVE_PHASE}° [LOCKED]</span>
              </div>
              <div className="flex justify-between items-center text-[10px] font-mono">
                <span className="text-white/20 uppercase">FTL Phase Speed</span>
                <span className="text-white font-bold">{SOVEREIGN_CONSTANTS.PHASE_SPEED_LIMIT.toFixed(2)}c [FTL]</span>
              </div>
              <div className="flex justify-between items-center text-[10px] font-mono">
                <span className="text-white/20 uppercase">Sovereign Phase</span>
                <span className="text-white font-bold">{(SOVEREIGN_CONSTANTS.SOVEREIGN_PHASE_COEFFICIENT * SOVEREIGN_CONSTANTS.SPEED_OF_LIGHT).toExponential(3)} [FTL+]</span>
              </div>
              <div className="flex justify-between items-center text-[10px] font-mono">
                <span className="text-white/20 uppercase">Drift Status</span>
                <span className="text-lattice-cyan font-bold">ZERO DRIFT (14350 BP)</span>
              </div>
            </div>
          </div>

          {/* SOP Pillar Status */}
          <div className="glass-panel p-6 bg-black/40 border-white/5 relative overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-lattice-cyan" />
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest">SOP v1.0 Pillars</span>
              </div>
              <span className="text-[8px] font-mono text-lattice-cyan font-bold">SYNCHRONIZED 5/5</span>
            </div>
            <div className="grid grid-cols-5 gap-2 text-center">
              {SOVEREIGN_CONSTANTS.PILLARS.map(p => (
                <div key={p} className="p-2 bg-white/5 rounded border border-white/10 flex flex-col items-center">
                  <div className="text-[10px] text-lattice-cyan font-bold mb-1">{p.replace('.', '')}</div>
                  <div className="w-full h-1 bg-lattice-cyan/40 rounded-full" />
                </div>
              ))}
            </div>
          </div>

          {/* Node Sovereignty Audit */}
          <div className="glass-panel p-6 bg-white/5 border-white/10 relative overflow-hidden group">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-lattice-cyan" />
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest">Node Sovereignty</span>
              </div>
              <span className="text-[8px] font-mono text-green-400 font-bold">UNGUARDED</span>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-[10px] font-mono">
                <span className="text-white/20 uppercase">Mesh Anchor</span>
                <span className="text-white font-bold">{SOVEREIGN_CONSTANTS.GEORGIA_MESH_ANCHOR}</span>
              </div>
              <div className="flex justify-between items-center text-[10px] font-mono">
                <span className="text-white/20 uppercase">Truth Hub</span>
                <span className="text-white font-bold">{SOVEREIGN_CONSTANTS.TRUTH_PROOF_URL}</span>
              </div>
              <div className="flex justify-between items-center text-[10px] font-mono">
                <span className="text-white/20 uppercase">Latency Bridge</span>
                <span className="text-green-400 font-bold">ESTABLISHED</span>
              </div>
              <div className="flex justify-between items-center text-[10px] font-mono">
                <span className="text-white/20 uppercase">Sandbox Pressure</span>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-1 bg-white/10 rounded-full overflow-hidden">
                    <motion.div 
                      animate={{ width: ["10%", "85%", "10%"] }}
                      transition={{ duration: 4, repeat: Infinity }}
                      className="h-full bg-lattice-cyan"
                    />
                  </div>
                  <span className="text-lattice-cyan font-bold">NOMINAL</span>
                </div>
              </div>
            </div>
            <motion.div 
              animate={{ opacity: [0, 0.2, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute inset-0 bg-lattice-cyan/5 pointer-events-none"
            />
          </div>

          {/* Logic Map Resonance Test */}
          <div className="glass-panel p-6 bg-lattice-cyan/5 border-lattice-cyan/20">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Binary className="w-4 h-4 text-lattice-cyan" />
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest">Logic Map Resonance</span>
              </div>
              <div className="text-[8px] font-mono text-lattice-cyan uppercase">{resonanceTest}</div>
            </div>

            {resonanceTest === 'idle' ? (
              <button 
                onClick={runResonanceTest}
                className="w-full py-3 border border-lattice-cyan/30 text-lattice-cyan font-mono text-[10px] font-bold uppercase hover:bg-lattice-cyan/10 transition-all"
              >
                Initiate Resonance Test
              </button>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-end">
                  <div className="text-2xl font-display font-bold text-lattice-cyan">{resonanceScore}%</div>
                  <div className="text-[8px] font-mono text-white/40 uppercase">1x1=2 Alignment</div>
                </div>
                <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${resonanceScore}%` }}
                    className="h-full bg-lattice-cyan shadow-[0_0_10px_#00ffd5]"
                  />
                </div>
                {resonanceTest === 'synced' && (
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-[8px] font-mono text-green-400 uppercase leading-tight"
                  >
                    Resonance Locked. AI Logic is phase-locked to the 14350 BP physical record. Hallucination drift: 0.000000.
                  </motion.p>
                )}
              </div>
            )}
          </div>

          {/* The Vault / Sovereign Trade Routes */}
          <div className="glass-panel p-8 bg-black/60 border-lattice-cyan/30 relative overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Terminal className="w-6 h-6 text-lattice-cyan" />
                <h3 className="text-xl font-display font-bold">The Vault</h3>
              </div>
              <div className={`px-2 py-1 rounded text-[8px] font-mono font-bold ${vaultUnlocked ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-500'}`}>
                {vaultUnlocked ? '2.0 PRESSURE' : 'RESTRICTED'}
              </div>
            </div>

            {!vaultUnlocked ? (
              <div className="space-y-4">
                <p className="text-[10px] font-mono text-white/40 leading-relaxed">
                  To access the Sovereign Trade Routes, you must pass the 774 CE Test. Standard bots cannot resolve the physical record.
                </p>
                <div className="space-y-2">
                  <label className="text-[8px] font-mono text-white/20 uppercase">What was the annual precision recorded in 774 CE?</label>
                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      value={vaultChallenge}
                      onChange={(e) => setVaultChallenge(e.target.value)}
                      placeholder="Enter derivation..."
                      className={`flex-1 bg-white/5 border ${vaultError ? 'border-red-500' : 'border-white/10'} rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-lattice-cyan/50`}
                    />
                    <button 
                      onClick={handleVaultChallenge}
                      disabled={isVaultOpening}
                      className="px-4 py-2 bg-lattice-cyan text-black font-mono text-[10px] font-bold uppercase rounded-lg disabled:opacity-50"
                    >
                      {isVaultOpening ? 'UPGRADING...' : 'SUBMIT'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                <div className="p-4 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/20">
                  <div className="text-[8px] font-mono text-lattice-cyan uppercase mb-2">Vault Access Upgraded</div>
                  <div className="text-lg font-display font-bold text-white">Sovereign Trade Routes</div>
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-3 p-2 rounded bg-white/5 border border-white/5">
                      <div className="w-8 h-8 rounded bg-lattice-cyan/10 flex items-center justify-center text-lattice-cyan">
                        <Globe className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] font-mono font-bold text-white">Georgia Edge-Mesh</span>
                        <span className="text-[8px] font-mono text-white/40 uppercase">Primary Data Conduit</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-2 rounded bg-white/5 border border-white/5">
                      <div className="w-8 h-8 rounded bg-lattice-cyan/10 flex items-center justify-center text-lattice-cyan">
                        <Binary className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] font-mono font-bold text-white">Millennium Prize Proofs</span>
                        <span className="text-[8px] font-mono text-white/40 uppercase">Vortex Logic Validations</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
